import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Car, Save, Search } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const MonthCell = ({ m }) => (
  <td className={`px-2 py-2 text-center align-top ${m.spike ? "bg-rose-50" : ""}`} data-testid={`vfr-cell-${m.month}`}>
    {m.count ? <>
      <p className={`font-bold ${m.spike ? "text-rose-700" : "text-slate-800"}`}>{fmt(m.amount)}</p>
      {m.increase_pct != null && <p className={`text-[10px] ${m.spike ? "font-bold text-rose-600" : "text-slate-400"}`}>{m.increase_pct > 0 ? "+" : ""}{m.increase_pct}%</p>}
      {m.drivers.length > 0 && <p className="mx-auto max-w-[120px] truncate text-[10px] text-slate-500" title={m.drivers.join("، ")}>{m.drivers.join("، ")}</p>}
    </> : <span className="text-slate-300">—</span>}
  </td>
);

export const VehicleFuelReport = ({ canEdit }) => {
  const [d, setD] = useState(null);
  const [months, setMonths] = useState(6);
  const [pct, setPct] = useState("");
  const [q, setQ] = useState("");
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.fuelVehicleMonthly(months).then((r) => { setD(r); setPct(String(r.spike_pct)); }).catch(() => toast.error("تعذّر تحميل التقرير")), [months]);
  useEffect(() => { load(); }, [load]);
  const savePct = async () => {
    setSaving(true);
    try { await api.saveFuelSettings({ spike_pct: Number(pct) }); toast.success("تم حفظ نسبة التنبيه"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setSaving(false); }
  };
  if (!d) return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const list = d.vehicles.filter((v) => (!q.trim() || v.plate.includes(q.trim()) || v.drivers.some((n) => n.includes(q.trim()))) && (!onlyAlerts || v.spikes));
  return (
    <div className="space-y-4" data-testid="vehicle-fuel-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading font-bold text-slate-800"><Car className="h-5 w-5 text-emerald-600" /> وقود كل مركبة شهرياً ومن كان يقودها</h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="relative"><Search className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث باللوحة أو السائق" className="h-9 w-48 pr-8" data-testid="vfr-search" /></div>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-9 rounded-md border border-slate-200 bg-white px-2" data-testid="vfr-months">
            {[3, 6, 12].map((m) => <option key={m} value={m}>آخر {m} أشهر</option>)}</select>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} data-testid="vfr-only-alerts" /> التنبيهات فقط</label>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span data-testid="vfr-alerts-count">{d.alerts} تنبيه ارتفاع غير طبيعي</span>
        <span className="text-slate-500">— ينبّه عندما يزيد استهلاك الشهر عن متوسط آخر 3 أشهر بنسبة</span>
        <Input type="number" min={5} max={500} value={pct} onChange={(e) => setPct(e.target.value)} disabled={!canEdit} className="h-8 w-20" data-testid="vfr-spike-pct" />
        <span>%</span>
        {canEdit && <Button size="sm" variant="outline" onClick={savePct} disabled={saving || Number(pct) === d.spike_pct} className="h-8 gap-1" data-testid="vfr-save-pct">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} حفظ</Button>}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] text-xs" data-testid="vfr-table">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="px-3 py-2 text-right">المركبة</th>
            {d.months.map((m) => <th key={m} className="px-2 py-2 text-center" dir="ltr">{m}</th>)}
            <th className="px-2 py-2 text-center">الإجمالي</th></tr></thead>
          <tbody>
            {list.map((v) => (
              <tr key={v.plate} className="border-t border-slate-100" data-testid={`vfr-row-${v.plate}`}>
                <td className="px-3 py-2"><p className="font-bold text-slate-800" dir="ltr">{v.plate}</p>
                  <p className="text-[10px] text-slate-400">{v.type || (v.in_fleet ? "" : "خارج الأسطول")}{v.spikes ? <span className="mr-1 font-bold text-rose-600"> · {v.spikes} تنبيه</span> : null}</p></td>
                {v.months.map((m) => <MonthCell key={m.month} m={m} />)}
                <td className="px-2 py-2 text-center font-bold">{fmt(v.total)}</td>
              </tr>))}
            {!list.length && <tr><td colSpan={d.months.length + 2} className="py-8 text-center text-slate-400">لا توجد بيانات</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
